"use client";

import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

export default function HeroSlider({ sliders = [] }) {
  const [api, setApi] = useState();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  const autoplay = Autoplay({
    delay: 5000,
    stopOnInteraction: false,
  });

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  const handleSlideClick = (link) => {
    if (link) {
      if (link.startsWith('http')) {
        window.open(link, '_blank');
      } else {
        window.location.href = link;
      }
    }
  };

  if (!sliders) {
    return null;
  }
  if (sliders.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full ">
      <Carousel
        setApi={setApi}
        className="w-full "
        opts={{
          align: "start",
          loop: true,
        }}
        plugins={[autoplay]}
      >
        <CarouselContent className="">
          {sliders.map((slider, index) => (
            <CarouselItem key={slider.id ? slider.id : `slider-${index}`} className="">
              <div 
                className="relative w-full  cursor-pointer group"
                onClick={() => handleSlideClick(slider.link)}
              >
                {/* Desktop Image */}
                <img
                  src={slider.desktopImage}
                  alt={`Slide ${index + 1}`}
                  className="hidden md:block w-full  object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  loading={index === 0 ? "eager" : "lazy"}
                />
                
                {/* Mobile Image */}
                <img
                  src={slider.mobileImage ? slider.mobileImage : slider.desktopImage}
                  alt={`Slide ${index + 1} mobile`}
                  className="block md:hidden w-full  object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  loading={index === 0 ? "eager" : "lazy"}
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30 group-hover:from-black/20 group-hover:to-black/20 transition-all duration-500" />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* Custom Navigation */}
        {sliders.length > 1 && (
          <>
            <button
              onClick={() => api?.scrollPrev()}
              className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-2 md:p-3 rounded-full transition-all duration-300 hover:scale-110 border border-white/20 flex items-center justify-center"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-4 h-4 md:w-6 md:h-6" />
            </button>

            <button
              onClick={() => api?.scrollNext()}
              className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-2 md:p-3 rounded-full transition-all duration-300 hover:scale-110 border border-white/20 flex items-center justify-center"
              aria-label="Next slide"
            >
              <ChevronRight className="w-4 h-4 md:w-6 md:h-6" />
            </button>

            {/* Slide Indicators */}
            <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2 md:gap-3">
              {sliders.map((_, index) => (
                <button
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  className={`h-3 md:h-2 rounded-full transition-all duration-300 cursor-pointer touch-manipulation ${
                    index === current - 1 
                      ? 'w-8 md:w-8 bg-white' 
                      : 'w-3 bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </Carousel>
    </div>
  );
}